/**
 * PhotoTile.jsx — one frame on the contact sheet or the filmstrip, plus the
 * delegated host handlers (click, double-click, drag, long-press) both hosts share.
 *
 * Tiles take primitive props only and are memoized, so a selection change on a
 * 1000-photo library re-renders just the tiles whose badge or outline changed.
 */

import { memo, useCallback, useEffect, useRef } from 'react';
import {
  commitAssign, getState, gotoUse, openPeek, rangeSelect, selectAllFam, toggleSel,
} from '../../hooks/useCarouselStore';
import { aspectLabel } from '../../lib/carousel/library.js';
import { filterString, normalizeLook } from '../../lib/carousel/look.js';

// Enough extra scale for a turned thumbnail to still cover its 3:2 tile.
function tileCover(rot) {
  const t = (rot * Math.PI) / 180;
  return Math.abs(Math.cos(t)) + Math.abs(Math.sin(t)) * 1.5;
}

function PhotoTile({ id, thumb, name, w, h, fam, seqStr, variant, ar, order, focus, cur, uses, rot, filter }) {
  const shot = (rot || filter)
    ? {
      transform: rot ? 'rotate(' + rot + 'deg) scale(' + tileCover(rot).toFixed(3) + ')' : undefined,
      filter: filter || undefined,
    }
    : undefined;
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: the listbox host holds focus; arrow keys move the .focus outline
    <div
      className={'tile' + (focus ? ' focus' : '') + (cur ? ' is-cur' : '')}
      data-id={id}
      role="option"
      aria-selected={order > 0 ? 'true' : 'false'}
      draggable
      title={name + ' · ' + w + '×' + h}
    >
      <div className="shot">
        <img src={thumb} alt="" decoding="async" draggable={false} style={shot} />
        {order > 0 && <span className="ord">{order}</span>}
        {ar && <span className="ar">{ar}</span>}
        <button type="button" className="peekbtn" tabIndex={-1} data-act="peek" aria-label={'View ' + name + ' large'}>⤢</button>
        {uses && <button type="button" className="uses" tabIndex={-1} data-act="uses">{uses}</button>}
        {uses && <span className="usedbar" />}
      </div>
      <div className="cap">
        <i>{fam}</i>
        <b>{seqStr || name}</b>
        <i>{variant}</i>
      </div>
    </div>
  );
}

const MemoTile = memo(PhotoTile);
export default MemoTile;

// t: { selIndex, focusId, curId, used }
export function renderTile(im, t) {
  const i = t.selIndex.get(im.id);
  const u = t.used.get(im.id);
  const look = normalizeLook(im.look);
  return (
    <MemoTile
      key={im.id}
      id={im.id}
      thumb={im.thumb}
      name={im.name}
      w={im.w}
      h={im.h}
      fam={im.fam}
      seqStr={im.seqStr}
      variant={im.variant}
      ar={aspectLabel(im)}
      order={i === undefined ? 0 : i + 1}
      focus={t.focusId === im.id}
      cur={t.curId === im.id}
      uses={u ? '▸' + u.map((x) => x + 1).join(',') : ''}
      rot={look.rot}
      filter={filterString(look.adj)}
    />
  );
}

// Delegated handlers for a host of tiles. Long-press (450ms) opens the peek and
// swallows the click that ends the press.
export function useTileHost() {
  const lp = useRef({ timer: null, teardown: null, swallow: null, touch: false });

  const cancelLongPress = useCallback(() => {
    const s = lp.current;
    clearTimeout(s.timer);
    s.timer = null;
    if (s.teardown) { const done = s.teardown; s.teardown = null; done(); }
  }, []);

  useEffect(() => cancelLongPress, [cancelLongPress]);

  const onClick = useCallback((e) => {
    const act = e.target.closest('[data-act]');
    const tile = e.target.closest('.tile');
    if (act && tile) {
      e.stopPropagation();
      if (act.dataset.act === 'peek') openPeek([tile.dataset.id]); else gotoUse(tile.dataset.id);
      return;
    }
    const all = e.target.closest('[data-selectall]');
    if (all) { selectAllFam(all.dataset.selectall); return; }
    if (!tile) return;
    const id = tile.dataset.id;
    if (lp.current.swallow === id) { lp.current.swallow = null; return; }
    const s = getState();
    if (s.assign) { commitAssign(id); return; }
    if (e.shiftKey && s.anchorId) rangeSelect(id);
    else toggleSel(id);
  }, []);

  const onDoubleClick = useCallback((e) => {
    // On a finger a second tap is how you take a mis-tap back — a tile has no
    // other deselect — so double-tap must not also throw up the full-screen peek.
    // The long-press and the corner ⤢ already cover "show me this bigger".
    if (lp.current.touch) return;
    const tile = e.target.closest('.tile');
    if (tile) openPeek([tile.dataset.id]);
  }, []);

  const onDragStart = useCallback((e) => {
    const tile = e.target.closest('.tile');
    if (!tile) return;
    cancelLongPress();
    e.dataTransfer.setData('text/x-img', tile.dataset.id);
    e.dataTransfer.effectAllowed = 'copy';
    const img = tile.querySelector('img');
    if (img && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(img, 66, 44);
  }, [cancelLongPress]);

  const onPointerDown = useCallback((e) => {
    const tile = e.target.closest('.tile');
    if (!tile || e.target.closest('[data-act]')) return;
    cancelLongPress();
    const s = lp.current;
    s.swallow = null;
    s.touch = e.pointerType !== 'mouse';
    const id = tile.dataset.id;
    const x = e.clientX, y = e.clientY;
    const mv = (ev) => { if (Math.abs(ev.clientX - x) + Math.abs(ev.clientY - y) > 10) cancelLongPress(); };
    const end = () => cancelLongPress();
    // the press may end anywhere - on the peek it just opened, or after a native drag
    s.teardown = () => {
      window.removeEventListener('pointermove', mv);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('dragend', end);
    };
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    window.addEventListener('dragend', end);
    s.timer = setTimeout(() => {
      s.swallow = id;
      openPeek([id]);
      cancelLongPress();
    }, 450);
  }, [cancelLongPress]);

  return { onClick, onDoubleClick, onDragStart, onPointerDown, onScroll: cancelLongPress };
}
